import { useEffect, useState, useCallback, useMemo } from 'react'
import {
  Users, Building2, ArrowDownToLine, ArrowUpFromLine,
  Activity, Clock, RefreshCw, ShieldCheck,
} from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { useYetki } from '@/hooks/useYetki'
import { YETKILER } from '@/lib/yetkiler'
import { tumPersonelleriGetir } from '@/lib/services/personel-service'
import { tumSubeleriGetir } from '@/lib/services/sube-service'
import {
  bugunkuTumKayitlar,
  sonKayitHaritasi,
  type Kayit,
} from '@/lib/services/kayit-service'
import type { Vardiyali, Sube, KullaniciPersonel } from '@/types'
import { BekleyenExtraKart } from '@/components/extra/BekleyenExtraKart'

export function YoneticiOverview() {
  const { kullanici } = useAuth()
  const benimSubeIdleri = useMemo(() => {
    if (kullanici?.rol === 'boss') return null  // BOSS tüm şubeleri görür
    if (kullanici?.rol !== 'personel') return []
    return (kullanici as KullaniciPersonel).subeIdleri ?? []
  }, [kullanici])

  const kayitGor = useYetki(YETKILER.KAYIT_GECMIS_GOR)

  const [personeller, setPersoneller] = useState<Vardiyali[]>([])
  const [subeler, setSubeler] = useState<Sube[]>([])
  const [bugunkuKayitlar, setBugunkuKayitlar] = useState<Kayit[]>([])
  const [yukleniyor, setYukleniyor] = useState(true)
  const [yenileniyor, setYenileniyor] = useState(false)
  const [sonGuncelleme, setSonGuncelleme] = useState<Date>(new Date())
  const [hata, setHata] = useState<string | null>(null)

  const [su_an, setSuAn] = useState<Date>(new Date())
  useEffect(() => {
    const t = setInterval(() => setSuAn(new Date()), 1000)
    return () => clearInterval(t)
  }, [])

  const yukle = useCallback(async (sessiz = false) => {
    if (!sessiz) setYukleniyor(true)
    setYenileniyor(true)
    try {
      const [p, s, k] = await Promise.all([
        tumPersonelleriGetir(),
        tumSubeleriGetir(),
        bugunkuTumKayitlar(),
      ])
      const filtrele = <T extends { subeIdleri?: string[]; subeId?: string }>(arr: T[], subeKey: 'subeIdleri' | 'subeId'): T[] => {
        if (benimSubeIdleri === null) return arr
        return arr.filter(x => {
          if (subeKey === 'subeIdleri') {
            const ids = x.subeIdleri ?? []
            return ids.some(id => benimSubeIdleri.includes(id))
          }
          return x.subeId !== undefined && benimSubeIdleri.includes(x.subeId)
        })
      }
      const benimSubeler = benimSubeIdleri === null ? s : s.filter(x => benimSubeIdleri.includes(x.id))
      const benimPersoneller = filtrele(p.filter(x => x.aktif), 'subeIdleri')
      const benimKayitlar = filtrele(k, 'subeId')

      setSubeler(benimSubeler)
      setPersoneller(benimPersoneller)
      setBugunkuKayitlar(benimKayitlar)
      setSonGuncelleme(new Date())
      setHata(null)
    } catch (err) {
      console.error(err)
      setHata('Veriler yüklenemedi.')
    } finally {
      setYukleniyor(false)
      setYenileniyor(false)
    }
  }, [benimSubeIdleri])

  useEffect(() => { yukle() }, [yukle])

  useEffect(() => {
    const i = setInterval(() => yukle(true), 30000)
    return () => clearInterval(i)
  }, [yukle])

  const sonKayitlar = useMemo(() => sonKayitHaritasi(bugunkuKayitlar), [bugunkuKayitlar])

  const aktifPersoneller = useMemo(() => {
    return personeller.filter(p => sonKayitlar.get(p.id)?.tip === 'giris')
  }, [personeller, sonKayitlar])

  const personelHaritasi = useMemo(() => {
    const m = new Map<string, Vardiyali>()
    personeller.forEach(p => m.set(p.id, p))
    return m
  }, [personeller])

  const subeHaritasi = useMemo(() => {
    const m = new Map<string, Sube>()
    subeler.forEach(s => m.set(s.id, s))
    return m
  }, [subeler])

  const bugunGirisSayisi = bugunkuKayitlar.filter(k => k.tip === 'giris').length
  const bugunCikisSayisi = bugunkuKayitlar.filter(k => k.tip === 'cikis').length
  const sonHareketler = bugunkuKayitlar.slice(0, 8)

  const tarihStr = su_an.toLocaleDateString('tr-TR', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  })
  const saatStr = su_an.toLocaleTimeString('tr-TR', {
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  })

  if (yukleniyor) {
    return (
      <div className="card text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 mb-3"></div>
        <p className="text-gray-600">Yükleniyor...</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-brand-600" />
            {subeler.length === 1 ? subeler[0]!.isim : `Yönetici Paneli (${subeler.length} şube)`}
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Son güncelleme: {sonGuncelleme.toLocaleTimeString('tr-TR')}
            {' · '}
            <button
              onClick={() => yukle()}
              disabled={yenileniyor}
              className="text-brand-600 hover:underline disabled:opacity-50 inline-flex items-center gap-1"
            >
              <RefreshCw className={`w-3 h-3 ${yenileniyor ? 'animate-spin' : ''}`} />
              Yenile
            </button>
          </p>
        </div>
        <div className="bg-gradient-to-br from-brand-500 to-indigo-600 text-white rounded-2xl px-6 py-3 shadow-md min-w-[220px] flex flex-col items-center justify-center text-center">
          <p className="text-3xl font-bold tabular-nums leading-none">{saatStr}</p>
          <p className="text-xs opacity-90 capitalize whitespace-nowrap mt-1.5">{tarihStr}</p>
        </div>
      </div>

      {hata && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4">
          {hata}
        </div>
      )}

      {kullanici && (
        <BekleyenExtraKart
          onaylayanId={kullanici.id}
          subeler={subeler}
          subeFiltre={benimSubeIdleri}
          onYenile={() => yukle()}
        />
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetrikKart ikon={Activity} renk="green" baslik="Şu An Aktif" deger={aktifPersoneller.length} altMetin={`/ ${personeller.length} kişi`} />
        <MetrikKart ikon={Users} renk="indigo" baslik="Toplam Personel" deger={personeller.length} />
        <MetrikKart ikon={ArrowDownToLine} renk="blue" baslik="Bugün Giriş" deger={bugunGirisSayisi} />
        <MetrikKart ikon={ArrowUpFromLine} renk="orange" baslik="Bugün Çıkış" deger={bugunCikisSayisi} />
      </div>

      <div>
        <h2 className="text-lg font-bold text-gray-900 mb-3">Şu An İçerideki Personel</h2>
        {aktifPersoneller.length === 0 ? (
          <div className="card text-center py-8 text-gray-500">Şu an aktif personel yok.</div>
        ) : (
          <div className="card divide-y divide-gray-100">
            {aktifPersoneller.map(p => {
              const son = sonKayitlar.get(p.id)
              const dakikaIceride = son ? Math.floor((Date.now() - son.zaman.toMillis()) / 60000) : 0
              const saat = Math.floor(dakikaIceride / 60)
              const dk = dakikaIceride % 60
              return (
                <div key={p.id} className="py-3 first:pt-0 last:pb-0 flex items-center justify-between">
                  <span className="font-medium text-gray-900">{p.isim}</span>
                  <span className="text-sm text-gray-600 flex items-center gap-1 bg-green-50 px-3 py-1 rounded-full">
                    <Clock className="w-3 h-3" />
                    {saat > 0 && `${saat}s `}{dk}dk
                  </span>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {kayitGor && (
        <div>
          <h2 className="text-lg font-bold text-gray-900 mb-3">Bugünkü Son Hareketler</h2>
          {sonHareketler.length === 0 ? (
            <div className="card text-center py-8 text-gray-500">Bugün henüz hareket yok.</div>
          ) : (
            <div className="card divide-y divide-gray-100">
              {sonHareketler.map(k => {
                const personel = personelHaritasi.get(k.personelId)
                return (
                  <div key={k.id} className="py-2.5 first:pt-0 last:pb-0 flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      k.tip === 'giris' ? 'bg-green-100' : 'bg-orange-100'
                    }`}>
                      {k.tip === 'giris' ? (
                        <ArrowDownToLine className="w-4 h-4 text-green-700" />
                      ) : (
                        <ArrowUpFromLine className="w-4 h-4 text-orange-700" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {personel?.isim ?? 'Bilinmeyen'}
                        <span className="text-gray-500 font-normal ml-2">
                          {k.tip === 'giris' ? 'giriş yaptı' : 'çıkış yaptı'}
                        </span>
                        {k.yontem !== 'qr' && (
                          <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">
                            manuel
                          </span>
                        )}
                      </p>
                      {subeler.length > 1 && (
                        <p className="text-xs text-gray-500 truncate">
                          {subeHaritasi.get(k.subeId)?.isim ?? '?'}
                        </p>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 flex-shrink-0">
                      {k.zaman.toDate().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {subeler.length > 0 && (
        <div className="space-y-2">
          {subeler.map(s => (
            <div key={s.id} className="card bg-gray-50 border-gray-200">
              <div className="flex items-start gap-3">
                <Building2 className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-gray-700">
                  <p className="font-semibold">{s.isim}</p>
                  <p className="text-gray-600">{s.adres}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

interface MetrikKartProps {
  ikon: typeof Users
  renk: 'green' | 'indigo' | 'blue' | 'orange'
  baslik: string
  deger: string | number
  altMetin?: string
}

const RENK_KLASSLARI: Record<string, { bg: string; text: string }> = {
  green: { bg: 'bg-green-100', text: 'text-green-700' },
  indigo: { bg: 'bg-indigo-100', text: 'text-indigo-700' },
  blue: { bg: 'bg-blue-100', text: 'text-blue-700' },
  orange: { bg: 'bg-orange-100', text: 'text-orange-700' },
}

function MetrikKart({ ikon: Icon, renk, baslik, deger, altMetin }: MetrikKartProps) {
  const k = RENK_KLASSLARI[renk]!
  return (
    <div className="card p-3 md:p-4">
      <div className="flex items-center gap-2 mb-2">
        <div className={`p-1.5 rounded-lg ${k.bg}`}>
          <Icon className={`w-4 h-4 ${k.text}`} />
        </div>
        <p className="text-xs text-gray-500 font-medium">{baslik}</p>
      </div>
      <div className="flex items-baseline gap-1">
        <p className="text-2xl md:text-3xl font-bold text-gray-900">{deger}</p>
        {altMetin && <p className="text-xs text-gray-500">{altMetin}</p>}
      </div>
    </div>
  )
}
